import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      setError("Missing session id");
      return;
    }
    const sessionId = decodeURIComponent(match[1]);

    (async () => {
      try {
        const res = await fetch(`${API}/auth/google/session`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body?.detail || "Google sign-in failed");
        }
        await refresh();
        // Clean the URL so a refresh doesn't re-process the session_id
        window.history.replaceState(null, "", window.location.pathname);
        nav("/app", { replace: true });
      } catch (e) {
        setError(e.message);
      }
    })();
  }, [nav, refresh]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#F0F9FF] flex items-center justify-center p-6">
        <div data-testid="auth-callback-error" className="max-w-md text-center">
          <p className="font-mono text-xs uppercase tracking-widest text-[#FF3333]">/ Sign-in error</p>
          <h1 className="mt-2 font-heading font-black text-3xl tracking-tighter">{error}</h1>
          <a
            href="/login"
            className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-4 py-2 bg-black text-white btn-brutal"
          >
            Back to sign in
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F9FF] flex items-center justify-center p-6">
      <div data-testid="auth-callback-loading" className="flex flex-col items-center gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-[#3B82F6]" />
        <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">
          Completing Google sign-in…
        </p>
      </div>
    </div>
  );
}
